import { useEffect, useRef } from "react";

import { activeBeat, type BookTimelineLayout, type SceneBeat } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";

type Acquire = NonNullable<SceneBeat["acquires"]>[number];

interface TimelineAcquiresProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
}

const NONE: readonly Acquire[] = [];

const markerOf = (id: string): HTMLElement | null => document.querySelector<HTMLElement>(
  `[data-marker-part="disc"][data-character-id="${CSS.escape(id)}"]`,
);

/** While someone takes an animal's DNA, both portraits pulse. */
export function TimelineAcquires({ canvas, layout, getPlayback }: TimelineAcquiresProps) {
  const lit = useRef(new Set<string>());

  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const seen = lit.current;
    const unlight = (id: string): void => {
      if (markerOf(id) !== null) {
        delete markerOf(id)!.dataset.acquiring;
      }
      seen.delete(id);
    };
    const onFrame = (): void => {
      const beat = activeBeat(layout, getPlayback().playheadMs);
      const here = beat?.acquires ?? NONE;
      const shown = here.flatMap((item) => [item.characterId, item.fromCharacterId]);
      for (const id of [...seen].filter((known) => !shown.includes(known))) {
        unlight(id);
      }
      for (const id of shown) {
        const marker = markerOf(id);
        if (marker !== null) {
          marker.dataset.acquiring = "true";
          seen.add(id);
        }
      }
    };
    const afterPortraits = (): void => queueMicrotask(onFrame);
    canvas.addEventListener("timeline-frame", afterPortraits);
    return () => {
      canvas.removeEventListener("timeline-frame", afterPortraits);
      [...seen].forEach(unlight);
    };
  }, [canvas, getPlayback, layout]);

  return null;
}
